import React, { useState } from 'react';
import Footer from '../../../components/Footer';
import './DineStackClassic.css';
import VegIcon from '../../../components/VegIcon';
import OffersRenderer from '../../../components/OffersRenderer';

import DEFAULT_LOGO from '../../../assets/defaults/logo-default.jpg';
import DEFAULT_BG from '../../../assets/defaults/bg-default.jpg';
import DEFAULT_ITEM from '../../../assets/defaults/item-default.jpg';

const DineStackClassic = ({ data }) => {
    // 1. Safe Destructuring & Default Values
    const restaurant = data?.restaurant || {};
    const menus = data?.menus || [];

    // 2. Safe State Initialization
    const [activeMenuId, setActiveMenuId] = useState(menus[0]?.id);
    const [selectedItem, setSelectedItem] = useState(null);

    // 3. Early Return if no data (though parent should handle this)
    if (!data) return <div className="dsc-loading">Loading...</div>;

    // 4. Safe Active Menu Logic
    const activeMenu = menus.find(m => m.id === activeMenuId) || menus[0];

    return (
        <div className="dsc-container">
            {/* Header Section - Back to Top */}
            <header className="dsc-header" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url(${restaurant.backgroundImageUrl || DEFAULT_BG})` }}>
                <div className="dsc-header-content">
                    <div className="dsc-header-top">
                        <img src={restaurant.logoUrl || DEFAULT_LOGO} alt="Logo" className="dsc-logo" />
                        <div>
                            <h1 className="dsc-title" title={restaurant.name}>{restaurant.name || 'Restaurant Name'}</h1>
                            <p className="dsc-subtitle" title={restaurant.description}>{restaurant.description || ''}</p>
                        </div>
                    </div>
                    <div className="dsc-header-bottom">
                        {restaurant.address && <span className="dsc-badge">📍 {restaurant.address}</span>}
                        {restaurant.phoneNumber && <span className="dsc-badge">📞 {restaurant.phoneNumber}</span>}
                    </div>
                </div>
            </header>

            {/* Offers / Notes */}
            <OffersRenderer offers={data?.offers} />

            {/* Navigation */}
            {menus.length > 1 && (
                <div className="dsc-nav-wrapper">
                    <nav className="dsc-nav">
                        {menus.map(menu => (
                            <button
                                key={menu.id}
                                className={`dsc-nav-item ${activeMenuId === menu.id ? 'active' : ''}`}
                                onClick={() => setActiveMenuId(menu.id)}
                            >
                                {menu.name}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Menu Content */}
            <main className="dsc-main">
                {activeMenu ? (
                    <div className="dsc-menu-body">
                        {activeMenu.categories?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(category => (
                            <section key={category.id} className="dsc-category-section">
                                <h3 className="dsc-category-title">{category.name}</h3>
                                <div className="dsc-grid">
                                    {category.items?.map(item => (
                                        <DscItem key={item.id} item={item} onClick={() => setSelectedItem(item)} />
                                    ))}
                                </div>
                            </section>
                        ))}

                        {activeMenu.uncategorizedItems?.length > 0 && (
                            <section className="dsc-category-section">
                                <h3 className="dsc-category-title">Others</h3>
                                <div className="dsc-grid">
                                    {activeMenu.uncategorizedItems.map(item => (
                                        <DscItem key={item.id} item={item} onClick={() => setSelectedItem(item)} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        No menu items available.
                    </div>
                )}
            </main>

            {selectedItem && (
                <div className="dsc-modal-overlay" onClick={() => setSelectedItem(null)}>
                    <div className="dsc-modal" onClick={e => e.stopPropagation()}>
                        <button className="dsc-modal-close" onClick={() => setSelectedItem(null)}>✕</button>
                        <div className="dsc-modal-img">
                            <img src={selectedItem.imageUrl || DEFAULT_ITEM} alt={selectedItem.name} />
                        </div>
                        <div className="dsc-modal-body">
                            <h3>{selectedItem.name}</h3>
                            <p className="dsc-modal-price">₹{(selectedItem.price || 0).toLocaleString()}</p>
                            <p className="dsc-modal-desc">{selectedItem.description}</p>

                            <div className="dsc-modal-variants">
                                <h4>Customizations</h4>
                                {selectedItem.variants?.filter(v => v.isActive).map(v => (
                                    <div key={v.id} className="dsc-v-row">
                                        <span>{v.name}</span>
                                        <span className="dsc-v-price">
                                            {v.price > 0 ? `₹${v.price.toLocaleString()}` : 'Included'}
                                        </span>
                                    </div>
                                ))}
                                {(!selectedItem.variants || selectedItem.variants.filter(v => v.isActive).length === 0) && (
                                    <p className="no-variants">Standard serving. No customizations.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

const DscItem = ({ item, onClick }) => {
    if (!item) return null;

    return (
        <div className={`dsc-card ${!item.isAvailable ? 'unavailable' : ''}`} onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className="dsc-image-wrapper">
                <img src={item.imageUrl || DEFAULT_ITEM} alt={item.name} loading="lazy" />
                {/* Safe Price Display */}
                <span className="dsc-price-badge">₹{(item.price || 0).toLocaleString()}</span>

                {/* Tags Badge */}
                {item.tags && item.tags.length > 0 && (
                    <div className="dsc-tags-container">
                        {item.tags.map(tag => (
                            <span key={tag} className="dsc-tag">{tag}</span>
                        ))}
                    </div>
                )}
            </div>
            <div className="dsc-card-content">
                <div className="dsc-item-header">
                    {(item.isVeg !== undefined && item.isVeg !== null) && (
                        <span className={`dsc-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                            <span className="dsc-veg-dot"></span>
                        </span>
                    )}
                    <h4 className="dsc-item-name" title={item.name}>{item.name}</h4>
                </div>
                <p className="dsc-desc" title={item.description}>{item.description}</p>
            </div>
        </div>
    );
};

export default DineStackClassic;
